<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  totalConversations: {
    type: Number,
    default: 0,
  },
  totalBreaches: {
    type: Number,
    default: 0,
  },
  firstResponseBreaches: {
    type: Number,
    default: 0,
  },
  resolutionBreaches: {
    type: Number,
    default: 0,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const { t } = useI18n();

const cssRgb = variableName => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
  return value ? value.split(/\s+/).join(', ') : '';
};

const brandColor = shade => {
  const docStyle = getComputedStyle(document.documentElement);
  const wootColor = docStyle.getPropertyValue(`--woot-${shade}`).trim();
  if (wootColor) return wootColor;

  return `rgb(${cssRgb('--n-brand-rgb')})`;
};

const slateColor = (shade, alpha = 1) => {
  const rgb = cssRgb(`--slate-${shade}`);
  return alpha === 1 ? `rgb(${rgb})` : `rgba(${rgb}, ${alpha})`;
};

const percentFormatter = computed(
  () =>
    new Intl.NumberFormat(navigator.language, {
      style: 'percent',
      maximumFractionDigits: 0,
    })
);

const chartRows = computed(() => [
  {
    label: t('SLA_REPORTS.INFRACTION_CHART.TOTAL'),
    value: props.totalConversations,
  },
  {
    label: t('SLA_REPORTS.INFRACTION_CHART.BREACHED'),
    value: props.totalBreaches,
  },
  {
    label: t('SLA_REPORTS.INFRACTIONS.TPR'),
    value: props.firstResponseBreaches,
  },
  {
    label: t('SLA_REPORTS.INFRACTIONS.TR'),
    value: props.resolutionBreaches,
  },
]);

const chartData = computed(() => ({
  labels: chartRows.value.map(row => row.label),
  datasets: [
    {
      label: t('SLA_REPORTS.INFRACTION_CHART.DATASET_LABEL'),
      data: chartRows.value.map(row => row.value),
      backgroundColor: context => {
        const { ctx, chartArea } = context.chart;
        if (!chartArea) return null;

        const gradient = ctx.createLinearGradient(
          chartArea.left,
          0,
          chartArea.right,
          0
        );
        gradient.addColorStop(0, brandColor(800));
        gradient.addColorStop(1, brandColor(500));
        return gradient;
      },
      hoverBackgroundColor: () => brandColor(600),
      borderRadius: 4,
      barThickness: 24,
    },
  ],
}));

const chartMaxValue = computed(() =>
  Math.max(...chartRows.value.map(row => row.value), 0)
);

const barEndPercentPlugin = {
  id: 'slaBarEndPercent',
  afterDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);

    ctx.save();
    ctx.font = '500 12px sans-serif';
    ctx.fillStyle = slateColor(12);
    ctx.textBaseline = 'middle';

    meta.data.forEach((bar, index) => {
      const value = Number(dataset.data[index]) || 0;
      const percent = props.totalConversations
        ? value / props.totalConversations
        : 0;
      const label = percentFormatter.value.format(percent);
      const x = Math.min(
        bar.x + 10,
        chartArea.right - ctx.measureText(label).width
      );

      ctx.textAlign = 'left';
      ctx.fillText(label, x, bar.y);
    });

    ctx.restore();
  },
};

const chartOptions = computed(() => ({
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: context =>
          t('SLA_REPORTS.INFRACTION_CHART.TOOLTIP', {
            value: new Intl.NumberFormat(navigator.language).format(
              context.parsed.x
            ),
          }),
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      suggestedMax: Math.ceil(chartMaxValue.value * 1.12),
      grid: { color: slateColor(7, 0.35) },
      ticks: {
        color: slateColor(11),
        precision: 0,
      },
    },
    y: {
      grid: { display: false },
      ticks: { color: slateColor(12) },
    },
  },
}));
</script>

<template>
  <div class="bg-n-solid-3 rounded-lg p-4">
    <h3 class="text-n-slate-12 font-medium mb-4">
      {{ $t('SLA_REPORTS.INFRACTION_CHART.TITLE') }}
    </h3>

    <div v-if="isLoading" class="flex items-center justify-center h-56">
      <Spinner />
      <span class="text-sm text-n-slate-11">
        {{ $t('SLA_REPORTS.LOADING') }}
      </span>
    </div>

    <div v-else-if="totalConversations" class="h-56">
      <Bar
        :data="chartData"
        :options="chartOptions"
        :plugins="[barEndPercentPlugin]"
      />
    </div>

    <p v-else class="text-n-slate-11 text-center py-8">
      {{ $t('SLA_REPORTS.NO_RECORDS') }}
    </p>
  </div>
</template>
